/**
 * MyProfile Component
 *
 * Allows agents to view and edit their profile information,
 * including their signature which auto-populates on PDF forms.
 */

import React, { useState, useEffect, useRef, useCallback } from 'react';
import { authFetch } from './authFetch';
import SignatureCapture from './components/SignatureCapture';

const BASE_URL = process.env.REACT_APP_API_BASE_URL;

function MyCommissionsSection() {
  const [summary, setSummary] = useState(null);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showRecords, setShowRecords] = useState(false);

  useEffect(() => {
    const fetchCommissions = async () => {
      try {
        const [summaryRes, recordsRes] = await Promise.all([
          authFetch(`${BASE_URL}/api/commissions/my-summary/`),
          authFetch(`${BASE_URL}/api/commissions/my-commissions/?page_size=25`),
        ]);
        if (summaryRes.ok) setSummary(await summaryRes.json());
        if (recordsRes.ok) {
          const data = await recordsRes.json();
          setRecords(data.results || []);
        }
      } catch (e) {
        console.error('Error fetching commissions:', e);
      } finally {
        setLoading(false);
      }
    };
    fetchCommissions();
  }, []);

  if (loading) return null;
  if (!summary || summary.record_count === 0) return null;

  const SOURCE_LABELS = {
    aca: 'ACA',
    medicare_advantage: 'Medicare Advantage',
    medicare_supplement: 'Medicare Supplemental',
  };

  return (
    <div className="mt-6">
      <div className="bg-white rounded-lg shadow p-6">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-lg font-semibold">My Commissions</h2>
          {records.length > 0 && (
            <button
              onClick={() => setShowRecords(!showRecords)}
              className="text-sm text-blue-600 hover:text-blue-800"
            >
              {showRecords ? 'Hide Details' : 'Show Details'}
            </button>
          )}
        </div>

        {/* Summary Cards */}
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-4">
          <div className="bg-green-50 p-4 rounded-lg">
            <div className="text-xs text-green-600 font-medium">Total Commissions</div>
            <div className="text-xl font-bold text-green-800">
              ${parseFloat(summary.total_comp || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
            </div>
          </div>
          <div className="bg-blue-50 p-4 rounded-lg">
            <div className="text-xs text-blue-600 font-medium">Total Flat Rate</div>
            <div className="text-xl font-bold text-blue-800">
              ${parseFloat(summary.total_flat_rate || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
            </div>
          </div>
          <div className="bg-purple-50 p-4 rounded-lg">
            <div className="text-xs text-purple-600 font-medium">Total Admin Fees</div>
            <div className="text-xl font-bold text-purple-800">
              ${parseFloat(summary.total_admin_fee || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
            </div>
          </div>
          <div className="bg-gray-50 p-4 rounded-lg">
            <div className="text-xs text-gray-600 font-medium">Total Records</div>
            <div className="text-xl font-bold text-gray-800">{summary.record_count}</div>
          </div>
        </div>

        {/* By Source Type */}
        {(summary.by_source || []).length > 0 && (
          <div className="flex gap-3 mb-4">
            {summary.by_source.map((s, i) => (
              <div key={i} className="text-xs bg-gray-100 px-3 py-1 rounded">
                <span className="font-medium">{SOURCE_LABELS[s.source_type] || s.source_type}:</span>{' '}
                ${parseFloat(s.total_comp || 0).toLocaleString('en-US', { minimumFractionDigits: 2 })}
                {' '}({s.record_count} records)
              </div>
            ))}
          </div>
        )}

        {/* Recent Records Table */}
        {showRecords && records.length > 0 && (
          <div className="overflow-x-auto border rounded">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-3 py-2 text-left font-medium text-gray-500">Subscriber</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-500">Product</th>
                  <th className="px-3 py-2 text-right font-medium text-gray-500">Comp Amount</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-500">Type</th>
                  <th className="px-3 py-2 text-left font-medium text-gray-500">Date</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200">
                {records.map((r) => (
                  <tr key={r.id} className="hover:bg-gray-50">
                    <td className="px-3 py-2">{r.subscriber_name || '-'}</td>
                    <td className="px-3 py-2">{r.product_type || '-'}</td>
                    <td className="px-3 py-2 text-right font-medium">${parseFloat(r.comp_amount || 0).toFixed(2)}</td>
                    <td className="px-3 py-2 text-xs">{SOURCE_LABELS[r.source_type] || r.source_type || '-'}</td>
                    <td className="px-3 py-2 text-xs text-gray-500">
                      {r.compensation_date || r.imported_at?.split('T')[0] || '-'}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

export default function MyProfile() {
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState(null);
  const [editMode, setEditMode] = useState(false);
  const [showSignatureCapture, setShowSignatureCapture] = useState(false);
  const [uploadingPhoto, setUploadingPhoto] = useState(false);

  const photoInputRef = useRef(null);

  // Form state
  const [formData, setFormData] = useState({
    first_name: '',
    last_name: '',
    npn: '',
    license_number: '',
    agency_name: '',
    phone: '',
    work_email: '',
    street_address: '',
    city: '',
    state: '',
    zip_code: '',
  });

  // Fetch profile on mount
  const fetchProfile = useCallback(async () => {
    try {
      setLoading(true);
      const response = await authFetch(`${BASE_URL}/api/profile/`);
      if (!response.ok) {
        throw new Error('Failed to fetch profile');
      }
      const data = await response.json();
      setProfile(data);
      setFormData({
        first_name: data.first_name || '',
        last_name: data.last_name || '',
        npn: data.npn || '',
        license_number: data.license_number || '',
        agency_name: data.agency_name || '',
        phone: data.phone || '',
        work_email: data.work_email || '',
        street_address: data.street_address || '',
        city: data.city || '',
        state: data.state || '',
        zip_code: data.zip_code || '',
      });
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchProfile();
  }, [fetchProfile]);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
  };

  // Save profile changes
  const handleSave = async () => {
    try {
      setSaving(true);
      setError(null);

      const response = await authFetch(`${BASE_URL}/api/profile/`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(formData),
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.detail || 'Failed to save profile');
      }

      const updatedProfile = await response.json();
      setProfile(updatedProfile);
      setEditMode(false);
      setSuccessMessage('Profile saved successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Handle signature capture
  const handleSignatureCapture = async (signatureData) => {
    try {
      setSaving(true);
      setError(null);

      const response = await authFetch(`${BASE_URL}/api/profile/`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          signature_image: signatureData.imageBase64,
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to save signature');
      }

      const updatedProfile = await response.json();
      setProfile(updatedProfile);
      setShowSignatureCapture(false);
      setSuccessMessage('Signature saved successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  // Handle photo upload
  const handlePhotoUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;

    // Validate file type
    if (!file.type.startsWith('image/')) {
      setError('Please select an image file');
      return;
    }

    // Validate file size (max 5MB)
    if (file.size > 5 * 1024 * 1024) {
      setError('Image must be less than 5MB');
      return;
    }

    try {
      setUploadingPhoto(true);
      setError(null);

      const formData = new FormData();
      formData.append('photo', file);

      const response = await authFetch(`${BASE_URL}/api/profile/photo/`, {
        method: 'POST',
        body: formData,
      });

      if (!response.ok) {
        throw new Error('Failed to upload photo');
      }

      const updatedProfile = await response.json();
      setProfile(updatedProfile);
      setSuccessMessage('Photo uploaded successfully');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setUploadingPhoto(false);
    }
  };

  // Clear signature
  const handleClearSignature = async () => {
    if (!window.confirm('Are you sure you want to clear your signature?')) {
      return;
    }

    try {
      setSaving(true);
      setError(null);

      const response = await authFetch(`${BASE_URL}/api/profile/`, {
        method: 'PATCH',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          signature_image: '',
        }),
      });

      if (!response.ok) {
        throw new Error('Failed to clear signature');
      }

      const updatedProfile = await response.json();
      setProfile(updatedProfile);
      setSuccessMessage('Signature cleared');
      setTimeout(() => setSuccessMessage(null), 3000);
    } catch (err) {
      setError(err.message);
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="text-lg text-gray-600">Loading profile...</div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto">
      <h1 className="text-2xl font-bold mb-6">My Profile</h1>

      {/* Success/Error Messages */}
      {successMessage && (
        <div className="mb-4 p-3 bg-green-100 text-green-700 rounded">
          {successMessage}
        </div>
      )}
      {error && (
        <div className="mb-4 p-3 bg-red-100 text-red-700 rounded">
          {error}
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        {/* Left Column - Photo and Signature */}
        <div className="space-y-6">
          {/* Profile Photo */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4">Profile Photo</h2>
            <div className="flex flex-col items-center">
              {profile?.photo_url ? (
                <img
                  src={profile.photo_url}
                  alt="Profile"
                  className="w-32 h-32 rounded-full object-cover mb-4"
                />
              ) : (
                <div className="w-32 h-32 rounded-full bg-gray-200 flex items-center justify-center mb-4">
                  <span className="text-4xl text-gray-400">
                    {profile?.first_name?.[0] || profile?.username?.[0] || '?'}
                  </span>
                </div>
              )}
              <input
                type="file"
                ref={photoInputRef}
                onChange={handlePhotoUpload}
                accept="image/*"
                className="hidden"
              />
              <button
                onClick={() => photoInputRef.current?.click()}
                disabled={uploadingPhoto}
                className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:bg-gray-400"
              >
                {uploadingPhoto ? 'Uploading...' : 'Change Photo'}
              </button>
            </div>
          </div>

          {/* Signature */}
          <div className="bg-white rounded-lg shadow p-6">
            <h2 className="text-lg font-semibold mb-4">Agent Signature</h2>
            <p className="text-sm text-gray-600 mb-4">
              Your signature will automatically appear on PDF forms you sign.
            </p>

            {profile?.signature_image ? (
              <div className="space-y-4">
                <div className="border rounded p-4 bg-gray-50">
                  <img
                    src={profile.signature_image}
                    alt="Your signature"
                    className="max-w-full h-auto"
                    style={{ maxHeight: '100px' }}
                  />
                </div>
                <p className="text-xs text-gray-500">
                  Last updated:{' '}
                  {profile.signature_updated_at
                    ? new Date(profile.signature_updated_at).toLocaleString()
                    : 'Unknown'}
                </p>
                <div className="flex gap-2">
                  <button
                    onClick={() => setShowSignatureCapture(true)}
                    className="px-3 py-1 bg-blue-600 text-white text-sm rounded hover:bg-blue-700"
                  >
                    Update Signature
                  </button>
                  <button
                    onClick={handleClearSignature}
                    className="px-3 py-1 bg-gray-200 text-gray-700 text-sm rounded hover:bg-gray-300"
                  >
                    Clear
                  </button>
                </div>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="border-2 border-dashed border-gray-300 rounded p-8 text-center text-gray-500">
                  No signature on file
                </div>
                <button
                  onClick={() => setShowSignatureCapture(true)}
                  className="w-full px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Add Signature
                </button>
              </div>
            )}
          </div>
        </div>

        {/* Right Column - Profile Information */}
        <div className="lg:col-span-2">
          <div className="bg-white rounded-lg shadow p-6">
            <div className="flex justify-between items-center mb-6">
              <h2 className="text-lg font-semibold">Profile Information</h2>
              {!editMode ? (
                <button
                  onClick={() => setEditMode(true)}
                  className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
                >
                  Edit
                </button>
              ) : (
                <div className="flex gap-2">
                  <button
                    onClick={() => {
                      setEditMode(false);
                      // Reset form data
                      setFormData({
                        first_name: profile?.first_name || '',
                        last_name: profile?.last_name || '',
                        npn: profile?.npn || '',
                        license_number: profile?.license_number || '',
                        agency_name: profile?.agency_name || '',
                        phone: profile?.phone || '',
                        work_email: profile?.work_email || '',
                        street_address: profile?.street_address || '',
                        city: profile?.city || '',
                        state: profile?.state || '',
                        zip_code: profile?.zip_code || '',
                      });
                    }}
                    className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleSave}
                    disabled={saving}
                    className="px-4 py-2 bg-green-600 text-white rounded hover:bg-green-700 disabled:bg-gray-400"
                  >
                    {saving ? 'Saving...' : 'Save'}
                  </button>
                </div>
              )}
            </div>

            {/* Account Info (read-only) */}
            <div className="mb-6 pb-6 border-b">
              <h3 className="text-sm font-medium text-gray-500 mb-3">Account</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-500">Username</label>
                  <p className="text-sm font-medium">{profile?.username}</p>
                </div>
                <div>
                  <label className="block text-xs text-gray-500">Login Email</label>
                  <p className="text-sm font-medium">{profile?.email}</p>
                </div>
              </div>
            </div>

            {/* Personal Info */}
            <div className="mb-6 pb-6 border-b">
              <h3 className="text-sm font-medium text-gray-500 mb-3">Personal Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">First Name</label>
                  {editMode ? (
                    <input
                      type="text"
                      name="first_name"
                      value={formData.first_name}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  ) : (
                    <p className="text-sm">{profile?.first_name || '-'}</p>
                  )}
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Last Name</label>
                  {editMode ? (
                    <input
                      type="text"
                      name="last_name"
                      value={formData.last_name}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  ) : (
                    <p className="text-sm">{profile?.last_name || '-'}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Agent Identification */}
            <div className="mb-6 pb-6 border-b">
              <h3 className="text-sm font-medium text-gray-500 mb-3">Agent Identification</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">NPN Number</label>
                  {editMode ? (
                    <input
                      type="text"
                      name="npn"
                      value={formData.npn}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  ) : (
                    <p className="text-sm">{profile?.npn || '-'}</p>
                  )}
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">License Number</label>
                  {editMode ? (
                    <input
                      type="text"
                      name="license_number"
                      value={formData.license_number}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  ) : (
                    <p className="text-sm">{profile?.license_number || '-'}</p>
                  )}
                </div>
                <div className="col-span-2">
                  <label className="block text-xs text-gray-500 mb-1">Agency Name</label>
                  {editMode ? (
                    <input
                      type="text"
                      name="agency_name"
                      value={formData.agency_name}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  ) : (
                    <p className="text-sm">{profile?.agency_name || '-'}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Contact Info */}
            <div className="mb-6 pb-6 border-b">
              <h3 className="text-sm font-medium text-gray-500 mb-3">Contact Information</h3>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Phone</label>
                  {editMode ? (
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  ) : (
                    <p className="text-sm">{profile?.phone || '-'}</p>
                  )}
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">Work Email</label>
                  {editMode ? (
                    <input
                      type="email"
                      name="work_email"
                      value={formData.work_email}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  ) : (
                    <p className="text-sm">{profile?.work_email || '-'}</p>
                  )}
                </div>
              </div>
            </div>

            {/* Address */}
            <div>
              <h3 className="text-sm font-medium text-gray-500 mb-3">Address</h3>
              <div className="grid grid-cols-2 gap-4">
                <div className="col-span-2">
                  <label className="block text-xs text-gray-500 mb-1">Street Address</label>
                  {editMode ? (
                    <input
                      type="text"
                      name="street_address"
                      value={formData.street_address}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  ) : (
                    <p className="text-sm">{profile?.street_address || '-'}</p>
                  )}
                </div>
                <div>
                  <label className="block text-xs text-gray-500 mb-1">City</label>
                  {editMode ? (
                    <input
                      type="text"
                      name="city"
                      value={formData.city}
                      onChange={handleInputChange}
                      className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    />
                  ) : (
                    <p className="text-sm">{profile?.city || '-'}</p>
                  )}
                </div>
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">State</label>
                    {editMode ? (
                      <input
                        type="text"
                        name="state"
                        value={formData.state}
                        onChange={handleInputChange}
                        maxLength={2}
                        className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    ) : (
                      <p className="text-sm">{profile?.state || '-'}</p>
                    )}
                  </div>
                  <div>
                    <label className="block text-xs text-gray-500 mb-1">Zip Code</label>
                    {editMode ? (
                      <input
                        type="text"
                        name="zip_code"
                        value={formData.zip_code}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border rounded focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                      />
                    ) : (
                      <p className="text-sm">{profile?.zip_code || '-'}</p>
                    )}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* My Commissions Section */}
      <MyCommissionsSection />

      {/* Signature Capture Modal */}
      {showSignatureCapture && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-lg p-6 max-w-lg w-full mx-4">
            <h3 className="text-lg font-semibold mb-4">Capture Signature</h3>
            <SignatureCapture
              label="Your Signature"
              signatureRole="agent"
              width={400}
              height={150}
              existingSignature={null}
              onCapture={handleSignatureCapture}
            />
            <div className="mt-4 flex justify-end">
              <button
                onClick={() => setShowSignatureCapture(false)}
                className="px-4 py-2 bg-gray-200 text-gray-700 rounded hover:bg-gray-300"
              >
                Cancel
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
